import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui-hud";
import { UploadCloud, ShieldCheck, Loader2, Check, XCircle, ArrowLeft, Camera, Info, Eye, Lock } from "lucide-react";

/**
 * /verify/identity — mandatory ID upload gate.
 *
 * Uploads a passport or driver's license, OCRs the printed name via Gemini
 * vision, compares to the account name. On match → `identity_verified=true`
 * and Hot Mic / DMs / applications unlock instantly.
 */
export default function VerifyIdentity() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/verify/identity/me", { silent402: true }).then((r) => setMe(r.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <p className="text-white/70 mb-4">Sign in to verify your identity.</p>
        <Link to="/login" className="btn-hud btn-hud-solid">SIGN IN</Link>
      </div>
    );
  }

  const alreadyVerified = me?.verified || user.identity_verified;

  const pickFile = (f) => {
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    if (f) {
      const isPdf = f.type === "application/pdf" || (f.name || "").toLowerCase().endsWith(".pdf");
      setPreview({ url: URL.createObjectURL(f), isPdf });
    } else {
      setPreview(null);
    }
    setResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Attach a photo of your passport or driver's license.");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/verify/identity", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.status === "verified") {
        toast.success("Identity verified — you're unlocked");
        await refresh();
        // Route back to where they came from after a short beat
        setTimeout(() => nav("/dashboard"), 1400);
      } else if (r.data.status === "name_mismatch") {
        toast.error("Name on ID didn't match your account name.");
      } else {
        toast.error("Couldn't read your ID — try a clearer photo.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  if (alreadyVerified) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="h-8 w-8 text-avail" />
            <div>
              <div className="overline text-avail">IDENTITY VERIFIED</div>
              <div className="font-display text-2xl font-bold">You're good.</div>
            </div>
          </div>
          <p className="text-white/70 mb-6">
            Your ID has been verified on <b className="text-white">{new Date(me?.verified_at || Date.now()).toLocaleString()}</b>.
            You can post, comment, DM, and apply to jobs.
          </p>
          <div className="flex gap-3">
            <Link to="/dashboard" className="btn-hud btn-hud-solid">GO TO COCKPIT</Link>
            <Link to="/hot-mic" className="btn-hud">Open Hot Mic</Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-14">
      <button
        type="button"
        onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
        data-testid="identity-back-btn"
        className="mb-6 inline-flex items-center gap-2 text-white/55 hover:text-white text-[12px] tracking-widest uppercase mono transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <div className="overline text-cyan mb-2">// IDENTITY · VERIFICATION</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-3">Prove you're you.</h1>
      <p className="text-white/70 mb-8 max-w-xl">
        PJP is a community of real, verified aviation professionals. To post in Hot Mic, DM other members, or apply to
        jobs, we need to confirm your legal name matches your account. Upload a photo of your passport or
        driver's license — verification is instant.
      </p>

      {result?.status === "name_mismatch" && (
        <Card className="!border-red-500/40 mb-6" testId="identity-mismatch-card">
          <div className="flex items-center gap-3 mb-2">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="font-display text-lg font-semibold">Name doesn't match</div>
          </div>
          <p className="text-white/75 text-sm mb-3">
            The ID reads <b className="text-white">{result.ocr_first_name} {result.ocr_last_name}</b> but your
            account is registered as <b className="text-white">{user.first_name} {user.last_name}</b>.
          </p>
          <p className="text-white/60 text-sm">
            If this is genuinely you (recent name change, married name, etc.), our team will review manually — otherwise
            update your PJP profile name to match your legal ID, then re-upload.
          </p>
        </Card>
      )}
      {result?.status === "unreadable" && (
        <Card className="!border-orange-hud/40 mb-6" testId="identity-unreadable-card">
          <div className="flex items-center gap-3 mb-2">
            <Eye className="h-5 w-5 text-orange-hud" />
            <div className="font-display text-lg font-semibold">Couldn't read the ID</div>
          </div>
          <p className="text-white/75 text-sm">
            The photo was too blurry or the ID wasn't fully visible. Try again with better lighting and all four corners in frame.
          </p>
        </Card>
      )}

      <Card>
        <form onSubmit={submit} className="space-y-5" data-testid="identity-verify-form">
          <label
            data-testid="identity-file-picker"
            className="block border-2 border-dashed border-white/15 hover:border-cyan/40 hover:bg-cyan/[0.03] rounded-xl cursor-pointer transition-colors overflow-hidden"
          >
            {preview ? (
              <div className="relative">
                {preview.isPdf ? (
                  <div className="w-full h-[420px] flex flex-col items-center justify-center bg-black/40 gap-3">
                    <div className="p-4 rounded-full border border-cyan/40 bg-cyan/10">
                      <UploadCloud className="h-8 w-8 text-cyan" />
                    </div>
                    <div className="mono text-[11px] tracking-widest uppercase text-white/70">PDF Attached</div>
                    <div className="text-[12px] text-white/50 max-w-xs text-center">First page will be rasterized and scanned when you submit.</div>
                  </div>
                ) : (
                  <img src={preview.url} alt="ID preview" className="w-full max-h-[420px] object-contain bg-black/40" />
                )}
                {/* Scanning animation overlay — only visible while OCR is running */}
                {busy && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="identity-scan-overlay">
                    {/* Cyan bar sweeping vertically */}
                    <div
                      className="absolute inset-x-0 h-1 shadow-[0_0_16px_4px_rgba(75,156,255,0.8)]"
                      style={{
                        background: "linear-gradient(90deg, transparent, #4B9CFF, #DCEBFF, #4B9CFF, transparent)",
                        animation: "id-scan 2.2s ease-in-out infinite",
                      }}
                    />
                    {/* Cyan tint */}
                    <div className="absolute inset-0 bg-cyan/[0.06]" />
                    {/* Corner brackets */}
                    <span className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-cyan" />
                    <span className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-cyan" />
                    <span className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-cyan" />
                    <span className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-cyan" />
                    {/* Status text */}
                    <div className="absolute inset-x-0 bottom-6 flex items-center justify-center">
                      <div className="mono text-[10px] tracking-[0.28em] uppercase text-cyan bg-obsidian/85 border border-cyan/40 rounded-full px-3 py-1 flex items-center gap-2">
                        <span className="relative inline-flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-70 animate-ping" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan" />
                        </span>
                        SCANNING · OCR IN PROGRESS
                      </div>
                    </div>
                  </div>
                )}
                <div className="absolute top-2 right-2 mono text-[10px] tracking-widest uppercase text-white/70 bg-black/50 px-2 py-1 rounded">
                  Tap to replace
                </div>
              </div>
            ) : (
              <div className="py-14 flex flex-col items-center gap-3">
                <div className="flex items-center gap-4">
                  <UploadCloud className="h-6 w-6 text-white/50" />
                  <Camera className="h-6 w-6 text-white/50" />
                </div>
                <div className="text-sm text-white/70">Tap to upload or take a photo of your ID</div>
                <div className="mono text-[10px] tracking-widest uppercase text-white/40">Passport · Driver's License · State ID · PDF</div>
              </div>
            )}
            <input
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={(e) => pickFile(e.target.files[0] || null)}
            />
          </label>

          <div className="grid sm:grid-cols-2 gap-3 text-[12px] text-white/60">
            <div className="flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-cyan" />
              <span>All four corners of the ID must be visible, no glare on the name.</span>
            </div>
            <div className="flex items-start gap-2">
              <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-cyan" />
              <span>We only extract the legal name. Your image is encrypted and never shared.</span>
            </div>
          </div>

          <div className="pt-3 border-t border-white/[0.06] flex justify-end">
            <button
              type="submit"
              disabled={!file || busy}
              data-testid="identity-verify-submit"
              className="btn-hud btn-hud-solid"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> VERIFY MY IDENTITY</>}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
